/**
 * The Tereiko tier offer — pricing v3, agreed 2026-07-12.
 *
 * The single frontend source for BOTH halves of the offer:
 *   - what the pricing surface displays (TIER_CARDS, FEATURE_MATRIX, copy)
 *   - what each tier actually grants (TIER_FEATURES, TIER_LIMITS)
 *
 * Every plan gate in the app reads the second half through `planHasFeature` /
 * `planLimit`, so a tier list is never spelled out at a call site. The backend's
 * /billing/subscription + /billing/usage endpoints stay authoritative for which
 * plan a workspace is ON and its live usage.
 *
 * TIER_FEATURES and TIER_LIMITS mirror backend `app/plans/catalog.py`. They are
 * a deliberate cross-language duplicate — the gate has to run client-side with
 * no round trip — and `backend/tests/unit/test_plan_catalog_parity.py` parses
 * THIS FILE and fails if the two ever disagree. Edit both together.
 */

export type TierId = 'free' | 'growth' | 'pro' | 'enterprise';

export const TIER_ORDER: TierId[] = ['free', 'growth', 'pro', 'enterprise'];

/** Older backends report 'starter'; it maps onto Growth for display. */
export const normalizeTierId = (raw: string): TierId => {
  const v = raw.toLowerCase();
  if (v === 'starter') return 'growth';
  return (TIER_ORDER as string[]).includes(v) ? (v as TierId) : 'free';
};

// ── Entitlements ────────────────────────────────────────────────────────────

/** Feature ids, spelled exactly as the backend catalog spells them. */
export const FEATURE = {
  scorm: 'scorm',
  bulkTools: 'bulk_tools',
  attributeFilters: 'attribute_filters',
  invitationEmails: 'invitation_emails',
  calendar: 'calendar',
  programAnalytics: 'program_analytics',
  certificateTemplate: 'certificate_template',
  certificateCustom: 'certificate_custom',
  leaderboard: 'leaderboard',
  prizeShop: 'prize_shop',
  auditLogs: 'audit_logs',
  dataExport: 'data_export',
  branding: 'branding',
  whiteLabel: 'white_label',
} as const;

export type FeatureId = (typeof FEATURE)[keyof typeof FEATURE];

/** Which features each tier grants. Parsed by the backend parity test. */
export const TIER_FEATURES: Record<TierId, FeatureId[]> = {
  free: [],
  growth: [
    'bulk_tools',
    'attribute_filters',
    'invitation_emails',
    'calendar',
    'certificate_template',
    'leaderboard',
  ],
  pro: [
    'bulk_tools',
    'attribute_filters',
    'invitation_emails',
    'calendar',
    'certificate_template',
    'leaderboard',
    'scorm',
    'program_analytics',
    'certificate_custom',
    'prize_shop',
    'audit_logs',
    'branding',
    'data_export',
  ],
  enterprise: [
    'bulk_tools',
    'attribute_filters',
    'invitation_emails',
    'calendar',
    'certificate_template',
    'leaderboard',
    'scorm',
    'program_analytics',
    'certificate_custom',
    'prize_shop',
    'audit_logs',
    'branding',
    'data_export',
    'white_label',
  ],
};

/** Numeric allowances; null means unlimited / uncapped by tier. */
export interface TierLimits {
  /** Hard cap on members from the plan tier (paid tiers cap on bought seats). */
  maxMembers: number | null;
  publishedCourses: number | null;
  publishedPrograms: number | null;
  aiGenerationsMonthly: number | null;
  aiGenerationsLifetime: number | null;
  assistantQuestionsMonthly: number | null;
  auditLogRetentionDays: number | null;
}

/** Parsed by the backend parity test — keep the keys in this shape. */
export const TIER_LIMITS: Record<TierId, TierLimits> = {
  free: {
    maxMembers: 5,
    publishedCourses: 3,
    publishedPrograms: 0,
    aiGenerationsMonthly: null,
    aiGenerationsLifetime: 1,
    assistantQuestionsMonthly: 25,
    auditLogRetentionDays: null,
  },
  growth: {
    maxMembers: null,
    publishedCourses: 15,
    publishedPrograms: 2,
    aiGenerationsMonthly: 3,
    aiGenerationsLifetime: null,
    assistantQuestionsMonthly: null,
    auditLogRetentionDays: null,
  },
  pro: {
    maxMembers: null,
    publishedCourses: null,
    publishedPrograms: null,
    aiGenerationsMonthly: 20,
    aiGenerationsLifetime: null,
    assistantQuestionsMonthly: null,
    auditLogRetentionDays: 90,
  },
  enterprise: {
    maxMembers: null,
    publishedCourses: null,
    publishedPrograms: null,
    aiGenerationsMonthly: null,
    aiGenerationsLifetime: null,
    assistantQuestionsMonthly: null,
    auditLogRetentionDays: null,
  },
};

/**
 * Whether a plan grants a feature. Normalizes first, so legacy ids (pre-v3
 * 'starter') and unknown values resolve the same way they do server-side.
 */
export const planHasFeature = (
  feature: FeatureId,
  plan: string | null | undefined
): boolean => TIER_FEATURES[normalizeTierId(plan ?? '')].includes(feature);

/** A plan's numeric allowance; null means unlimited. */
export const planLimit = (
  key: keyof TierLimits,
  plan: string | null | undefined
): number | null => TIER_LIMITS[normalizeTierId(plan ?? '')][key];

/** Canonical tier ids granting a feature — for nav items gated by `tiers`. */
export const tiersWithFeature = (feature: FeatureId): TierId[] =>
  TIER_ORDER.filter((id) => TIER_FEATURES[id].includes(feature));

/** Canonical tier ids whose allowance for `key` is not zero (null = unlimited).
 *  Programs are gated by their allowance rather than a feature flag. */
export const tiersWithAllowance = (key: keyof TierLimits): TierId[] =>
  TIER_ORDER.filter((id) => TIER_LIMITS[id][key] !== 0);

export interface TierCard {
  id: TierId;
  name: string;
  tagline: string;
  /** Monthly base price in EUR; null = custom (talk to us). */
  baseMonthly: number | null;
  /** Monthly per-seat price in EUR; null = custom or not applicable. */
  perSeatMonthly: number | null;
  /** Short note under the price; null on a plan we quote per deal. */
  priceNote: string | null;
  /** Worked example under the note; null on a plan we quote per deal. */
  example: string | null;
  cta: string;
  popular?: boolean;
}

export const TIER_CARDS: TierCard[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Try the whole core loop with a handful of people.',
    baseMonthly: 0,
    perSeatMonthly: null,
    priceNote: '5 seats included',
    example: 'any mix of trainers & learners',
    cta: 'Start free',
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'Your first real audience, motivated and certified.',
    baseMonthly: 19,
    perSeatMonthly: 2,
    priceNote: '€19 + €2 × seats / month',
    example: '25 people → €69/mo',
    cta: 'Choose Growth',
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Your brand, your academy — with the full toolkit.',
    baseMonthly: 49,
    perSeatMonthly: 4,
    priceNote: '€49 + €4 × seats / month',
    example: '25 people → €149/mo · 14-day free trial',
    cta: 'Try Pro free',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'White label, your own CSS, migration included, nothing capped.',
    baseMonthly: null,
    perSeatMonthly: null,
    priceNote: null,
    example: null,
    cta: 'Talk to us',
  },
];

/** A cell is either included (true), absent (false), or a short label. */
export type MatrixValue = boolean | string;

export interface MatrixRow {
  label: string;
  /** Fine print under the label; hidden on small screens. */
  sub?: string;
  /** Plain-language "what this means in practice" — shown in the ⓘ tooltip
   *  on the pricing surfaces (platform and web share this copy). */
  info: string;
  values: Record<TierId, MatrixValue>;
  /** Marks Free-tier teaser values (rendered in secondary orange). */
  hook?: boolean;
}

export interface MatrixGroup {
  title: string;
  rows: MatrixRow[];
}

export const FEATURE_MATRIX: MatrixGroup[] = [
  {
    title: 'People',
    rows: [
      {
        label: 'Seats',
        sub: 'trainer or learner — same seat, switch anytime',
        info: 'A seat is one person in your workspace — trainer or learner, the same seat covers both roles. Free includes 5 seats; on paid plans you pay per seat and add as many as you need.',
        values: { free: '5', growth: 'unlimited', pro: 'unlimited', enterprise: 'unlimited' },
      },
      {
        label: 'Attribute filters',
        sub: 'country, role, department…',
        info: 'Tag members with role, department, and location labels, then filter learner lists and analytics by them — for example "completion for the Sales team only".',
        values: { free: false, growth: true, pro: true, enterprise: true },
      },
      {
        label: 'Bulk tools',
        sub: 'CSV learner upload, bulk course assignment',
        info: 'Work in batches instead of one by one: upload hundreds of learners from a CSV file and assign a course to a whole group at once.',
        values: { free: false, growth: true, pro: true, enterprise: true },
      },
    ],
  },
  {
    title: 'Content',
    rows: [
      {
        label: 'Active courses',
        sub: 'published & visible; drafts never count',
        info: 'How many courses can be published — visible to learners — at the same time. Drafts are unlimited on every plan; only live courses count against the number.',
        values: { free: '3', growth: '15', pro: 'unlimited', enterprise: 'unlimited' },
      },
      {
        label: 'Course wizard, every core content type',
        info: 'Build courses from rich text, PDFs, images, videos, external links, quizzes, and feedback surveys — the full course editor is included on every plan.',
        values: { free: true, growth: true, pro: true, enterprise: true },
      },
      {
        label: 'SCORM packages',
        info: 'Upload training made in outside authoring tools (Articulate, Captivate…) in the industry-standard SCORM format, and track completions inside Tereiko.',
        values: { free: false, growth: false, pro: true, enterprise: true },
      },
      {
        label: 'Active learning programs',
        info: 'Multi-day learning journeys that bundle courses into a schedule — each day unlocks new content for the audience you enroll.',
        values: { free: false, growth: '2', pro: 'unlimited', enterprise: 'unlimited' },
      },
      {
        label: 'Certificates',
        info: "Learners earn a certificate PDF when they complete a course or program. Growth uses Tereiko's auto-generated template; Pro and above let you upload your own design.",
        values: { free: false, growth: 'Tereiko template', pro: 'custom design', enterprise: 'custom design' },
      },
    ],
  },
  {
    title: 'Engagement',
    rows: [
      {
        label: 'Chat',
        sub: 'direct & group messaging, attachments',
        info: 'Built-in messaging for everyone in the workspace — direct conversations and group rooms, with file attachments. Course invitations sent with chat delivery arrive here as cards.',
        values: { free: true, growth: true, pro: true, enterprise: true },
      },
      {
        label: 'Invitation emails',
        sub: "invites & reminders in learners' inboxes",
        info: 'When you assign a course, learners get the invitation and reminders in their email inbox — not only inside the platform.',
        values: { free: false, growth: true, pro: true, enterprise: true },
      },
      {
        label: 'Gamification',
        info: 'Learners earn points for completing content. Growth shows a leaderboard with monthly seasons; Pro adds custom point rules and unlocks the prize shop.',
        values: { free: false, growth: 'leaderboard only', pro: 'full', enterprise: 'full' },
      },
      {
        label: 'Prize shop',
        info: 'A reward catalog you stock yourself — learners spend their earned points to claim prizes, and you track and fulfil every claim.',
        values: { free: false, growth: false, pro: true, enterprise: true },
      },
    ],
  },
  {
    title: 'Insight',
    rows: [
      {
        label: 'Analytics dashboard',
        info: 'Progress and completion dashboards for courses and learners. The plan sets how far back you can look: the last 7 days on Free, 30 days on Growth, everything on Pro, custom date ranges on Enterprise.',
        values: { free: '7-day', growth: '30-day', pro: 'full presets', enterprise: 'full + custom ranges' },
      },
      {
        label: 'Excel exports',
        info: 'Download analytics as Excel files — for reports, stakeholders, or your own spreadsheets.',
        values: { free: false, growth: false, pro: true, enterprise: true },
      },
      {
        label: 'Program analytics',
        info: 'Day-by-day completion tracking for learning programs — see exactly where an audience advances or gets stuck.',
        values: { free: false, growth: false, pro: true, enterprise: true },
      },
      {
        label: 'Audit logs',
        info: 'A who-did-what history of important actions in the workspace (sign-ins, edits, role changes…) — for compliance and troubleshooting. Enterprise can also export it.',
        values: { free: false, growth: false, pro: '90-day history', enterprise: 'unlimited + export' },
      },
    ],
  },
  {
    title: 'AI',
    rows: [
      {
        label: 'Teri — the assistant',
        info: "Tereiko's built-in assistant. Ask how anything works, or about your own courses, learners, and progress — Teri answers from your workspace's real data.",
        values: { free: '25 questions/mo', growth: 'unlimited', pro: 'unlimited', enterprise: 'unlimited' },
        hook: true,
      },
      {
        label: 'AI course generation',
        info: 'Give Teri a topic and optional reference documents and it drafts a complete course — sections, content, and quizzes — ready for your review before anything is published. The Free allowance counts only courses generated while on Free — what you generated on a paid plan never uses it up.',
        values: { free: '1 course on Free', growth: '3 / month', pro: '20 / month', enterprise: 'unlimited' },
        hook: true,
      },
    ],
  },
  {
    title: 'Platform',
    rows: [
      {
        label: 'Branding',
        sub: 'on Pro a small “Powered by Tereiko” stays; white label removes it',
        info: "On Pro, your logo and brand colors replace Tereiko's across the workspace (a small “Powered by Tereiko” remains). Enterprise white label removes Tereiko's marks entirely.",
        values: { free: 'Tereiko', growth: 'Tereiko', pro: 'your logo & colors', enterprise: 'white label' },
      },
      {
        label: 'Calendar + Google integration',
        info: 'See course deadlines and events in a built-in calendar, and connect Google Calendar so they show up in your own calendar too.',
        values: { free: false, growth: true, pro: true, enterprise: true },
      },
      {
        label: 'Custom CSS',
        sub: 'custom JS as a managed service only',
        info: 'Enterprise workspaces can restyle the platform with their own CSS. Custom JavaScript is offered as a managed service — our team builds and maintains it for you.',
        values: { free: false, growth: false, pro: false, enterprise: true },
      },
      {
        label: 'Data migration',
        sub: 'we move your old LMS content for you',
        info: 'We move your existing courses, learners, and history from your old LMS into Tereiko for you — included on Enterprise, available as a paid add-on on Pro.',
        values: { free: false, growth: false, pro: 'paid add-on', enterprise: 'included' },
      },
      {
        label: 'Support',
        info: 'Where and how fast you get human help. Every plan includes the help center, Teri, and email support — on Free, email has no guaranteed response time; paid plans add one (48h on Growth, 12h priority on Pro, 4h critical with a success manager on Enterprise).',
        values: {
          free: 'help center · Teri · email (no SLA)',
          growth: 'email · 48h',
          pro: 'priority email · 12h',
          enterprise: 'success manager · 4h critical',
        },
      },
    ],
  },
];

export const FINE_PRINT =
  'Downgrades never delete anything — but they do ask you to choose: within 30 days, pick which members and published content stay within the new limits (learners keep learning throughout, and members return automatically if you upgrade again). Drafts are always unlimited. Account emails (passwords, sign-in) work on every tier. Annual billing on Growth and Pro: 2 months free.';
